import { defineComponent as _defineComponent } from 'vue'
import ReportConstructorWrap from "@/components/statistics/extended-report/ReportConstructorWrap.vue";
import ColumnsCheckboxes from "@/components/statistics/ColumnsCheckboxes.vue";
import { computed, onBeforeMount, ref, watch } from "vue";
import TrashBtn from "@/components/base/buttons/TrashBtn.vue";
import { useRoute, useRouter } from "vue-router/composables";
import PageTabs from "@/components/base/PageTabs.vue";
import { useWindowResize } from "@/utils/useWindowSize";
import { IColumnFilter } from "@/types";
import { HoldColumns, HoldFilters } from "@/services/TablePage/hold";
import FiltersDesign from "@/components/base/filters/FiltersDesign.vue";
import store from "@/store";
import {
    getEmployeesActiveProducts,
    getEmployeesCountries, getEmployeesDepartments, getEmployeesStats,
    getEmployeesTargetActions, getOfferTags,
    getOneEmployeesOffer,
} from "@/api/offers";
import { showServerError } from "@/utils";
import ReportFilterByDate from "@/components/statistics/extended-report/ReportFilterByDate.vue";
import { getUserDepartmentsV3 } from "@/api/user";


export default /*#__PURE__*/_defineComponent({
  __name: 'HoldConstructor',
  emits: ['submit', 'clear'],
  setup(__props, { expose, emit }) {

const route = useRoute();
const router = useRouter();
const selected = ref<string[]>([]);
const columns = ref<IColumnFilter[]>([]);
const filters = ref<HoldFilters>({});
const filtersComp = ref<FiltersDesign>(null);



const loadingDataForSelect = [
    { id: 'offer_country', errorName: 'Список стран не загружен', api: getEmployeesCountries },
    { id: 'offer_product', errorName: 'Список типов продуктов не загружен', api: getEmployeesActiveProducts },
    { id: 'offer_target_action', errorName: 'Список целевых действий не загружен', api: getEmployeesTargetActions },
    { id: 'offer_stats', errorName: 'Список типов статистики не загружен', api: getEmployeesStats },
    { id: 'offer_tag', errorName: 'Список тегов не загружен', api: getOfferTags },
    {
        id: 'offer_department',
        errorName: 'Список направлений не загружен',
        api: getEmployeesDepartments,
    },
    {
        id: 'master_manager_department_id',
        errorName: 'Список отделов не загружен',
        api: getUserDepartmentsV3,
    },
];

const currentTab = ref(0);
const tabs = [
    { tab: 'Группировка' },
    { tab: 'Фильтры' },
];
const changeTab = (tab: number) => currentTab.value = tab;

const { width } = useWindowResize();
const isMobile = computed(() => width.value < 577);

const getDateFilter = computed(() => {
    return filters.value.filters?.find((filter) => filter.id === 'date');
});

const emptyConditions = {
    isEmptyDate: !store.getters['GET_FILTERS_PARAMS_OBJECT'].date_from ||
        !store.getters['GET_FILTERS_PARAMS_OBJECT'].date_to,
};

const getEmptyConditions = computed(() => {
    return getDateFilter.value?.select?.length > 0 ? {} : emptyConditions;
});

onBeforeMount(() => {
    filters.value = new HoldFilters();
    columns.value = new HoldColumns().columns;
    setDataForSelect();
    setDefaultFilters();
    updateSelectedColumns();
});

const setDataForSelect = () => {
    for (const select of loadingDataForSelect) {
        getDataForSelect(select.api, select.id, select.errorName);
    }
};

const getDataForSelect = async (apiMethod: any, filterId: string | string[], errorName: string = 'Список не загружен'): Promise<void> => {
    const filterIds = Array.isArray(filterId) ? filterId : [filterId];

    try {
        const items = await apiMethod();
        const data = items.data || items;

        filterIds.forEach(id => {
            const filter = filters.value.filters.find(i => i.id === id);
            if (filter) {
                filter.items = data;
            }
        });
    } catch (err) {
        showServerError(err, errorName);
    }
};


const setDefaultFilters = () => {
    const filtersWithDefaults = filters.value.filters.filter((filter) => filter.defaultValue);
    const paramsObj = {};
    filtersWithDefaults.forEach((filter) => {
        filter.select = filter.defaultValue;
        if (filter.id === 'date') {
            const params = {
                date_from: filter.defaultValue[0],
                date_to: filter.defaultValue[1],
            };
            Object.assign(paramsObj, params);
        } else {
            let value: any = filter.defaultValue;
            if (filter.itemValue && Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                value = value.map((v: any) => v[filter.itemValue!]);
            }
            Object.assign(paramsObj, { [filter.id]: value });
        }
    });
    store.commit('SET_FILTERS_PARAMS_OBJECT', paramsObj);
};

const setDefaultColumns = () => {
    selected.value = columns.value.flatMap(i => i.items.filter(j => j.default).map(k => k.value));
    updateUrl();
};

const updateSelectedColumns = () => {
    const cols = route.query.columns;
    if (!cols || (Array.isArray(cols) && cols.length === 0)) {
        setDefaultColumns();
        return;
    }
    selected.value = cols;
};

watch(() => route.query, () => {
    updateUrl();
});

const updateUrl = () => {
    const data = { columns: [...selected.value] };
    const query = Object.keys(route.query)?.length > 0 ? { ...route.query, ...data } : data;
    router.replace({ name: router.currentRoute.name || '', query }).catch((error) => {
        if (error.name !== 'NavigationDuplicated') {
            throw error;
        }
    });
};

const submitFilters = () => {
    filtersComp.value.submit();
};

const clearFilters = () => {
    filters.value.clearFilters();
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    setDefaultFilters();
    emit('clear');
};

const clearAll = () => {
    filters.value.clearFilters();
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    setDefaultColumns();
    setDefaultFilters();
    emit('clear');
};

// Логика работы с целями оффера
const currentOffer = computed(() => {
    if (!filters.value.filters) return [];
    const offerFilter = filters.value.filters.find((filter) => filter.id === 'offer_id');
    return offerFilter ? offerFilter.select : [];
});

const getGoalFilter = computed(() => {
    return filters.value.filters?.find((filter) => filter.id === 'offer_goal_id');
});

watch(
    () => {
        const p = store.getters['GET_FILTERS_PARAMS_OBJECT'] as Record<string, unknown>;
        return [p?.date_from, p?.date_to] as const;
    },
    ([from, to]) => {
        const f = getDateFilter.value;
        if (!f || !from || !to) return;
        if (f.select?.length === 2 && f.select[0] === from && f.select[1] === to) return;
        f.select = [from as string, to as string];
    },
);

watch(() => currentOffer.value, () => {
    if (!filters.value.filters) return;
    getGoalFilter.value!.disabled = currentOffer.value.length <= 0;
    if (currentOffer.value.length <= 0) {
        getGoalFilter.value!.clear();
    }
    if (getGoalFilter.value && currentOffer.value.length > 1) {
        getGoalFilter.value!.tooltip = 'Фильтр по цели возможен только если отчёт отфильтрован по одному офферу';
    }
    if (getGoalFilter.value && currentOffer.value.length === 1) {
        setOfferData(currentOffer.value[0].legacy_id);
    }
}, { deep: true });

const setOfferData = async (id: number) => {
    try {
        const { goals } = await getOneEmployeesOffer(id, { by_legacy_id: 1 });
        setGoals(goals);
    } catch (e) {
        showServerError(e, 'Список целей оффера не загружен');
    }
};

const setGoals = (goals) => {
    getGoalFilter.value!.items = goals;

    if (route.query.hasOwnProperty('goal_id') && getGoalFilter.value!.select.length <= 0) {
        const goalIds = route.query.goal_id;
        if (!goalIds || goalIds === 'NaN') return;
        getGoalFilter.value!.select = Array.isArray(goalIds) ? goalIds.map(g => +g!) : [Number(goalIds)];
    }
};

expose({ selected });

return { __sfc: true,route, router, selected, columns, filters, filtersComp, emit, loadingDataForSelect, currentTab, tabs, changeTab, width, isMobile, getDateFilter, emptyConditions, getEmptyConditions, setDataForSelect, getDataForSelect, setDefaultFilters, setDefaultColumns, updateSelectedColumns, updateUrl, submitFilters, clearFilters, clearAll, currentOffer, getGoalFilter, setOfferData, setGoals, ReportConstructorWrap, ColumnsCheckboxes, TrashBtn, PageTabs, FiltersDesign, ReportFilterByDate }
}

})